import { MutationTree, GetterTree, ActionTree } from "vuex";
import { RootState } from "vuefront-store";
import gql from "graphql-tag";
import { KlaviyoCustomer, KlaviyoSettings } from "./types";
import { markOrderReported, shouldSkipView, wasOrderReported } from "./dedup";
import { safeParse } from "./utils";
import {
  ensureKlaviyoQueue,
  loadOnsiteScript,
  waitForOnsiteLoad,
} from "./script";
import {
  pushIdentify,
  pushTrack,
  trackViewedCategory as pushViewedCategory,
  trackViewedProduct as pushViewedProduct,
} from "./tracking-client";

export type { KlaviyoCustomer, KlaviyoSettings } from "./types";

const hasMarketingConsent = () =>
  typeof window !== "undefined" && window.vfConsent?.has("marketing") === true;
// Per-store locks keep SSR requests isolated and concurrent activation safe.
const settingsLoads = new WeakMap<State, Promise<void>>();
const bootstraps = new WeakMap<State, Promise<void>>();
const checkoutIdentities = new WeakMap<State, string>();
const pendingOrders = new Set<string>();

export type State = {
  settings: KlaviyoSettings;
  scriptLoaded: boolean;
  identified: boolean;
  loaded: boolean;
};

export const state = (): State => ({
  settings: {
    enabled: false,
    publicApiKey: "",
    customer: null,
    storefrontUrl: "",
  },
  scriptLoaded: false,
  identified: false,
  loaded: false,
});

export const getters: GetterTree<State, RootState> = {
  enabled(state) {
    return state.settings.enabled && !!state.settings.publicApiKey;
  },
  publicApiKey(state) {
    return state.settings.publicApiKey;
  },
  customer(state) {
    return state.settings.customer;
  },
  scriptLoaded(state) {
    return state.scriptLoaded;
  },
  identified(state) {
    return state.identified;
  },
  loaded(state) {
    return state.loaded;
  },
};

export const mutations: MutationTree<State> = {
  setSettings(state, payload: KlaviyoSettings) {
    state.settings = {
      enabled: !!payload.enabled,
      publicApiKey: payload.publicApiKey || "",
      customer: payload.customer || null,
      storefrontUrl: payload.storefrontUrl || "",
    };
  },
  setScriptLoaded(state, payload: boolean) {
    state.scriptLoaded = !!payload;
  },
  setIdentified(state, payload: boolean) {
    state.identified = !!payload;
  },
  setLoaded(state, payload: boolean) {
    state.loaded = !!payload;
  },
};

export const actions: ActionTree<State, RootState> = {
  async identifyCheckout(
    { dispatch, state, getters },
    fields: Array<{ name: string; value: string }>
  ) {
    if (!hasMarketingConsent() || !Array.isArray(fields)) return;
    const values = Object.fromEntries(
      fields.map((field) => [field.name, field.value])
    );
    const email = String(values.email || "")
      .trim()
      .toLowerCase();
    if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ||
      checkoutIdentities.get(state) === email
    )
      return;
    await dispatch("load");
    if (!hasMarketingConsent() || !getters.enabled) return;
    await dispatch("identify", {
      email,
      firstName: values.firstName,
      lastName: values.lastName,
    });
    checkoutIdentities.set(state, email);
  },
  load({ commit, dispatch, state }) {
    if (!hasMarketingConsent()) return;
    if (state.loaded) return dispatch("bootstrap");
    if (settingsLoads.has(state)) return settingsLoads.get(state);
    const pending = (async () => {
      try {
        const { data } = await this.$vfapollo.query({
          fetchPolicy: "network-only",
          query: gql`
            {
              klaviyoSettings {
                enabled
                publicApiKey
                storefrontUrl
                customer {
                  email
                  firstName
                  lastName
                  phone
                  languageId
                }
              }
            }
          `,
        });

        if (!hasMarketingConsent()) return;
        if (data?.klaviyoSettings) {
          commit("setSettings", data.klaviyoSettings);
          commit("setLoaded", true);
          if (
            data.klaviyoSettings.enabled &&
            data.klaviyoSettings.publicApiKey
          ) {
            await dispatch("bootstrap");
          }
        } else {
          commit("setLoaded", true);
        }
      } catch (e) {
        // Marketing availability must not replace the storefront with an error.
        // Leave settings retryable after a transient network failure.
        // eslint-disable-next-line no-console -- non-fatal diagnostic; intentionally a warning, not an error
        console.warn("[klaviyo] settings unavailable");
      }
    })();
    settingsLoads.set(state, pending);
    void pending.finally(() => settingsLoads.delete(state));
    return pending;
  },

  bootstrap({ commit, state, dispatch }) {
    if (!hasMarketingConsent()) return;
    if (!state.settings.enabled || !state.settings.publicApiKey) return;
    if (state.scriptLoaded) return;
    if (bootstraps.has(state)) return bootstraps.get(state);
    const pending = (async () => {
      ensureKlaviyoQueue();
      // Identification precedes queued ecommerce events even on slow downloads.
      if (!state.identified && state.settings.customer?.email) {
        pushIdentify(state.settings.customer);
        commit("setIdentified", true);
      }
      // Don't compete with hero/product images and hydration for bandwidth.
      await waitForOnsiteLoad();
      if (!hasMarketingConsent()) return;
      const ok = await loadOnsiteScript(state.settings.publicApiKey);
      if (!hasMarketingConsent()) return;
      commit("setScriptLoaded", ok);
      await dispatch("flushQueuedEvents");
    })();
    bootstraps.set(state, pending);
    void pending.finally(() => bootstraps.delete(state));
    return pending;
  },

  identify({ commit, getters }, customer: KlaviyoCustomer) {
    if (!hasMarketingConsent() || !getters.enabled) return;
    if (!customer || !customer.email) return;
    pushIdentify(customer);
    commit("setIdentified", true);
  },

  trackViewedProduct({ getters, state }, product: any) {
    if (!hasMarketingConsent() || !getters.enabled || !product || !product.id)
      return;
    if (shouldSkipView("Viewed Product", String(product.id))) return;
    pushViewedProduct(state.settings.storefrontUrl, product);
  },

  trackViewedCategory({ getters, state }, category: any) {
    if (!hasMarketingConsent() || !getters.enabled || !category || !category.id)
      return;
    if (shouldSkipView("Viewed Category", String(category.id))) return;
    pushViewedCategory(state.settings.storefrontUrl, category);
  },

  async trackSearchedSite(
    { getters },
    { term, totalResults }: { term: string; totalResults?: number }
  ) {
    if (!hasMarketingConsent() || !getters.enabled || !term) return;
    try {
      const { data } = await this.$vfapollo.query({
        fetchPolicy: "network-only",
        query: gql`
          query ($term: String, $totalResults: Int) {
            klaviyoSearchedSite(term: $term, totalResults: $totalResults) {
              enabled
              event
              properties
            }
          }
        `,
        variables: { term, totalResults: totalResults || 0 },
      });
      const payload = data?.klaviyoSearchedSite;
      if (!payload || !payload.enabled) return;
      const props = safeParse(payload.properties);
      if (props) pushTrack(payload.event || "Searched Site", props);
    } catch (e) {
      // eslint-disable-next-line no-console -- non-fatal tracking diagnostic; intentionally a warning, not an error
      console && console.warn && console.warn("[klaviyo] searchedSite", e);
    }
  },

  async trackAddedToCart(
    { getters },
    { productId, quantity }: { productId: string | number; quantity?: number }
  ) {
    if (!hasMarketingConsent() || !getters.enabled || !productId) return;
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: gql`
          mutation ($productId: String, $quantity: Int) {
            klaviyoAddedToCart(productId: $productId, quantity: $quantity) {
              enabled
              event
              properties
            }
          }
        `,
        variables: {
          productId: String(productId),
          quantity: quantity || 1,
        },
      });
      const payload = data?.klaviyoAddedToCart;
      if (!payload || !payload.enabled) return;
      const props = safeParse(payload.properties);
      if (props) pushTrack(payload.event || "Added to Cart", props);
    } catch (e) {
      // eslint-disable-next-line no-console -- non-fatal tracking diagnostic; intentionally a warning, not an error
      console && console.warn && console.warn("[klaviyo] addedToCart", e);
    }
  },

  async trackStartedCheckout({ getters }) {
    if (!hasMarketingConsent() || !getters.enabled) return;
    try {
      const { data } = await this.$vfapollo.query({
        fetchPolicy: "network-only",
        query: gql`
          {
            klaviyoStartedCheckout {
              enabled
              event
              properties
            }
          }
        `,
      });
      const payload = data?.klaviyoStartedCheckout;
      if (!payload || !payload.enabled) return;
      const props = safeParse(payload.properties);
      if (props) pushTrack(payload.event || "Started Checkout", props);
    } catch (e) {
      // eslint-disable-next-line no-console -- non-fatal tracking diagnostic; intentionally a warning, not an error
      console && console.warn && console.warn("[klaviyo] startedCheckout", e);
    }
  },

  async firePlacedOrder({ getters }, orderId: string | number) {
    if (!hasMarketingConsent() || !getters.enabled || !orderId) return;
    const idStr = String(orderId);
    if (wasOrderReported(idStr) || pendingOrders.has(idStr)) return;
    pendingOrders.add(idStr);
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: gql`
          mutation ($orderId: String) {
            klaviyoPlacedOrder(orderId: $orderId) {
              fired
            }
          }
        `,
        variables: { orderId: idStr },
      });
      if (data?.klaviyoPlacedOrder?.fired) markOrderReported(idStr);
    } catch (e) {
      // eslint-disable-next-line no-console -- non-fatal tracking diagnostic; intentionally a warning, not an error
      console && console.warn && console.warn("[klaviyo] placedOrder", e);
    } finally {
      pendingOrders.delete(idStr);
    }
  },

  async trackAddedToWishlist({ getters }, productId: string | number) {
    if (!hasMarketingConsent() || !getters.enabled || !productId) return;
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: gql`
          mutation ($productId: String) {
            klaviyoAddedToWishlist(productId: $productId) {
              enabled
              event
              properties
            }
          }
        `,
        variables: { productId: String(productId) },
      });
      const payload = data?.klaviyoAddedToWishlist;
      if (!payload || !payload.enabled) return;
      const props = safeParse(payload.properties);
      if (props) pushTrack(payload.event || "Added to Wishlist", props);
    } catch (e) {
      // eslint-disable-next-line no-console -- non-fatal tracking diagnostic; intentionally a warning, not an error
      console && console.warn && console.warn("[klaviyo] addedToWishlist", e);
    }
  },

  async subscribeNewsletter(
    _context,
    { email, status }: { email: string; status?: boolean }
  ): Promise<{ status: boolean; message?: string }> {
    // Newsletter consent is a first-party form submission handled by the
    // OpenCart API. It must keep working even when the visitor has declined
    // advertising cookies, so do not gate it on the onsite SDK or its consent
    // controlled settings bootstrap.
    if (!email) {
      return { status: false, message: "Email is required" };
    }
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: gql`
          mutation ($email: String, $status: Boolean) {
            klaviyoSubscribeNewsletter(email: $email, status: $status) {
              status
              message
            }
          }
        `,
        variables: {
          email,
          status: typeof status === "undefined" ? true : !!status,
        },
      });
      const result = data?.klaviyoSubscribeNewsletter;
      return {
        status: !!(result && result.status),
        message: result?.message || "",
      };
    } catch (e: any) {
      return { status: false, message: e?.message || "Subscription failed" };
    }
  },

  flushQueuedEvents() {
    // Reserved for future use: when bootstrap finishes, replay any events
    // that were queued before klaviyo.js loaded. The proxy handles this for
    // us today, but this hook makes it explicit if more buffering is added.
  },
};
